/**
 * Typed replacement for the remaining window.EventBus channels. The old bus events
 * 'show-snackbar'/'show-confirm' become useSnackbar/useConfirm composables, and
 * 'admin-toggle-drawer'/'route-update'/'show-loading' are owned by the new shell —
 * what remains are the app-level notification channels below, with identical payloads.
 */

export type AppEventPayloads = {
    /** Emitted by UserLogin (first unread notification on login); consumed by Notification.vue */
    'show-notification': { notification: { code?: string, type: string, data: any } }
    /** Emitted by App.vue when a realtime AppEvent arrives while online; consumed by Subscription.vue */
    'notification-received': { type: string, data: any }
}

type Handler<T> = (payload: T) => void

const handlers = new Map<keyof AppEventPayloads, Set<Handler<any>>>()

export function useAppEvents() {
    function on<K extends keyof AppEventPayloads>(event: K, handler: Handler<AppEventPayloads[K]>) {
        if (!handlers.has(event)) handlers.set(event, new Set())
        handlers.get(event)!.add(handler)
        return () => off(event, handler)
    }

    function off<K extends keyof AppEventPayloads>(event: K, handler: Handler<AppEventPayloads[K]>) {
        handlers.get(event)?.delete(handler)
    }

    function emit<K extends keyof AppEventPayloads>(event: K, ...payload: AppEventPayloads[K] extends void ? [] : [AppEventPayloads[K]]) {
        handlers.get(event)?.forEach(handler => handler(payload[0]))
    }

    return { on, off, emit }
}
